import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

import { db } from './firebase';
import { criticisms } from './data/criticisms';
import { PageLoadingSkeleton } from './components/common/LoadingState';
import ErrorBoundary from './components/common/ErrorBoundary';
import HamburgerMenu from './components/common/HamburgerMenu';
import HeroSection from './components/HeroSection';
import SubscribeForm from './components/SubscribeForm';
import TweetsFeed from './components/TweetsFeed';
import AdSense from './components/AdSense';
import './App.css';

// Lazy load page components
const LaborIssues = React.lazy(() => import('./components/issues/LaborIssues'));
const About = React.lazy(() => import('./components/About'));
const Contact = React.lazy(() => import('./components/Contact'));
const MarketGames = React.lazy(() => import('./components/issues/MarketGames'));
const FailedPromises = React.lazy(() => import('./components/issues/FailedPromises'));
const TwitterChaos = React.lazy(() => import('./components/issues/TwitterChaos'));
const Environmental = React.lazy(() => import('./components/issues/Environmental'));
const CovidStance = React.lazy(() => import('./components/issues/CovidStance'));
const TaxAvoidance = React.lazy(() => import('./components/issues/TaxAvoidance'));
const PersonalConduct = React.lazy(() => import('./components/issues/PersonalConduct'));
const BusinessEthics = React.lazy(() => import('./components/issues/BusinessEthics'));
const LegalRegulatory = React.lazy(() => import('./components/issues/LegalRegulatory'));
const NotFound = React.lazy(() => import('./components/NotFound'));

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Analytics Wrapper Component
function AnalyticsWrapper({ children }) {
  const location = useLocation();

  useEffect(() => {
    const analytics = getAnalytics();

    logEvent(analytics, 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname
    });

    if (location.pathname.includes('/issue/')) {
      logEvent(analytics, 'issue_view', {
        issue_name: location.pathname.split('/').pop(),
        issue_title: document.title
      });
    }
  }, [location]);

  return children;
}

// Visit tracker hook
function useVisitTracker() {
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await addDoc(collection(db, 'visits'), {
          timestamp: serverTimestamp(),
          page: window.location.pathname,
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          screenSize: `${window.innerWidth}x${window.innerHeight}`
        });
      } catch (error) {
        // Silently fail - visit tracking is non-critical
      }
    };

    trackVisit();
  }, []);
}

// Twitter widget loader hook
function useTwitterWidget() {
  useEffect(() => {
    if (window.twttr && window.twttr.widgets) {
      window.twttr.widgets.load();
    }
  }, []);
}

function App() {
  useVisitTracker();
  useTwitterWidget();

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AnalyticsWrapper>
          <ErrorBoundary>
            <div className="App">
              <div className="background-overlay">
                <div className="content-grid">
                  <header className="main-header">
                    <div className="header-content">
                      <HamburgerMenu criticisms={criticisms} />
                      <div className="header-title">
                        <h1>Genius or Fool?</h1>
                        <div className="subtitle">The Elon Musk Controversy</div>
                      </div>
                      <nav className="header-nav">
                        <Link to="/">Home</Link>
                        <Link to="/about">About</Link>
                        <Link to="/contact">Contact</Link>
                      </nav>
                    </div>
                  </header>

                  <div className="main-area">
                    <aside className="side-content">
                      <nav className="issues-nav" aria-label="Issues navigation">
                        <ul>
                          {criticisms.map(criticism => (
                            <li key={criticism.id}>
                              <Link to={`/issue/${criticism.id}`}>
                                {criticism.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </nav>

                      <SubscribeForm />
                      <TweetsFeed />

                      <div className="ad-container">
                        <AdSense
                          key="main-ad"
                          client="ca-pub-9231525824764429"
                          slot="6717516851"
                        />
                      </div>
                    </aside>

                    <main className="main-content">
                      <Suspense fallback={<PageLoadingSkeleton />}>
                        <Routes>
                          <Route path="/" element={<HeroSection />} />
                          <Route path="/about" element={<About />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/issue/personal-conduct" element={<PersonalConduct />} />
                          <Route path="/issue/business-ethics" element={<BusinessEthics />} />
                          <Route path="/issue/labor-issues" element={<LaborIssues />} />
                          <Route path="/issue/market-games" element={<MarketGames />} />
                          <Route path="/issue/failed-promises" element={<FailedPromises />} />
                          <Route path="/issue/twitter-chaos" element={<TwitterChaos />} />
                          <Route path="/issue/legal-regulatory" element={<LegalRegulatory />} />
                          <Route path="/issue/environmental" element={<Environmental />} />
                          <Route path="/issue/covid-stance" element={<CovidStance />} />
                          <Route path="/issue/tax-avoidance" element={<TaxAvoidance />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </main>
                  </div>
                </div>
              </div>
            </div>
          </ErrorBoundary>
        </AnalyticsWrapper>
      </Router>
    </HelmetProvider>
  );
}

export default App;
